import React, { useState } from "react";
import clsx from "clsx";

import Button from "components/common/Button";
import ModalContainer, { ModalContainerProps } from "components/common/ModalContainer";
import TabsContainer from "components/common/TabsContainer";
import { Space, SpaceMember } from "redux/Ldb/types/spaceTypes";
import SpaceAddEditModal from "./SpaceAddEditModal";
import useToggle from "hooks/useToggle";
import Divider from "components/common/Divider";
import Avatar from "components/common/Avatar";
import moment from "moment";
import Paper from "components/common/Paper";
import SpaceGuestBadge from "./SpaceGuestBadge";
import { defaultSynthMember } from "./constants/defaultSynthMember";
import Dropdown from "components/common/Dropdown";
import MenuItem from "components/common/MenuItem";
import IconButton from "components/common/IconButton";
import SearchTextField from "components/common/InputField/SearchTextField";
import { getFromStorage } from "helpers";
import SpaceDetailsSettingsArchiveModal from "./SpaceDetailsSettingsArchiveModal";
import SpaceDetailsSettingsLeaveModal from "./SpaceDetailsSettingsLeaveModal";
import { normalizeMembers } from "../../utils/spaceUtils";
import SpaceDetailsRemoveMembersModal from "./SpaceDetailsRemoveMembersModal";
import CustomToast from "components/common/CustomToast";
import toast from "react-hot-toast";
import { useUnarchiveSpace } from "hooks/spaceHooks";

type SpaceDetailsSettingsModalProps = { spaceData: Space } & Omit<ModalContainerProps, "children">;

export default function SpaceDetailsSettingsModal(props: SpaceDetailsSettingsModalProps) {
    const { closeModal, spaceData, ...rest } = props;
    const userProfile = getFromStorage("ally-user");

    const [spaceAddEditModal, toggleSpaceAddEditModal] = useToggle();
    const [activeTab, setActiveTab] = useState(0);

    const members = spaceData?.members as SpaceMember[];
    const owner = members.find((member) => member?.isOwner) as SpaceMember;
    const isOwner = owner?.ldbId === userProfile?.user?._id;

    const contentProps = {
        spaceData,
        isOwner,
        owner,
    };

    const spaceTabs = [
        { title: "About", content: <SpaceDetailsSettingsAbout {...contentProps} /> },
        { title: "Members", content: <SpaceDetailsSettingsMembers {...contentProps} /> },
        { title: "Settings", content: <SpaceDetailsSettingsSettings {...contentProps} /> },
    ];

    return (
        <>
            {" "}
            <ModalContainer
                tailwindClassName="w-full max-w-[500px] max-h-[90vh]"
                closeModal={closeModal}
                {...rest}
            >
                <div className="w-full bg-white rounded-lg">
                    <div className="px-[24px]">
                        <div className="flex justify-between py-[12px] items-center gap-4">
                            <p className="text-lg font-rocGroteskMedium text-g-75 line-clamp-2 ">
                                {spaceData.name}
                            </p>

                            <i
                                className="ri-close-fill text-xl text-gm-50 cursor-pointer"
                                onClick={closeModal}
                            ></i>
                        </div>

                        {isOwner && (
                            <div className="flex gap-2">
                                {!spaceData?.isArchived && (
                                    <Button
                                        btnText="Edit Space"
                                        btnClassname="!py-2  !w-fit !px-4 !bg-slate-100 !text-g-75"
                                        onClick={() => toggleSpaceAddEditModal()}
                                        icon={<i className="ri-pencil-line text-[18px]"></i>}
                                    />
                                )}
                                {/* <Button
                                    btnText="Invite people"
                                    btnClassname="!py-2  !w-fit !px-4 !bg-slate-100 !text-g-75"
                                    onClick={() => {
                                        closeModal();
                                    }}
                                    icon={<i className="ri-user-add-line text-[18px]"></i>}
                                /> */}
                            </div>
                        )}
                    </div>

                    <div className="h-full scrollbar-hidden">
                        <TabsContainer
                            tabs={spaceTabs.map((tab) => tab.title)}
                            activeTab={activeTab}
                            setActiveTab={(idx) => {
                                setActiveTab(idx);
                            }}
                            disableTabParam
                            className="px-[24px] mt-[12px]"
                            itemClassName="!pb-2"
                            borderLineClase={"!text-slate-100 w-full px-[24px]"}
                        />

                        <div className="p-[24px] space-y-2 overflow-y-scroll h-[380px] scrollbar-hidden">
                            {spaceTabs?.[activeTab]?.content}
                        </div>
                    </div>
                </div>
            </ModalContainer>
            {spaceAddEditModal && (
                <SpaceAddEditModal
                    open={spaceAddEditModal}
                    closeModal={() => toggleSpaceAddEditModal()}
                    spaceData={spaceData}
                />
            )}
        </>
    );
}

function SpaceDetailsSettingsAbout(props: { spaceData: Space; owner: SpaceMember }) {
    const { spaceData, owner } = props;
    const [leaveSpaceModal, toggleLeaveSpaceModal] = useToggle();

    const createdBy = owner;

    return (
        <div>
            <div>
                <p className="text-sm font-rocGroteskMedium text-g-75">Description</p>
                <p className="text-xs font-rocGroteskMedium text-g-75 line-clamp-2 text-slate-500">
                    {spaceData?.description}
                </p>
            </div>

            <Divider className="my-[20px]" />

            <div>
                <p className="text-sm font-rocGroteskMedium text-g-75">Created by</p>
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Avatar
                            src={createdBy?.ldb?.profile?.avatar}
                            alt={createdBy?.ldb?.fullName}
                            className="w-[24px] h-[24px]"
                        />
                        <p className="text-xs font-rocGroteskMedium text-g-75">
                            {createdBy?.ldb?.fullName}
                        </p>
                    </div>

                    <p className="text-xs font-rocGroteskMedium text-g-75 text-slate-500">Admin</p>
                </div>
            </div>

            <Divider className="my-[20px]" />

            <div>
                <p className="text-sm font-rocGroteskMedium text-g-75">Created on</p>
                <p className="text-xs font-rocGroteskMedium text-g-75">
                    {moment(spaceData?.createdAt).format("DD MMM, YYYY")}
                </p>
            </div>

            <Divider className="my-[20px]" />

            <Paper className="flex items-center justify-between gap-1">
                <div>
                    <p className="text-sm font-rocGroteskMedium text-g-75">Leave space</p>
                    <p className="text-xs font-rocGroteskMedium text-g-75 text-slate-500">
                        You’ll no longer see this space or receive updates from it.
                    </p>
                </div>

                <Button
                    btnText="Leave space"
                    disabled={spaceData?.isArchived}
                    btnClassname="!w-fit whitespace-nowrap !py-[6px] !px-[12px] !bg-[#E4281D] !text-white"
                    onClick={() => toggleLeaveSpaceModal()}
                />
            </Paper>

            {leaveSpaceModal && (
                <SpaceDetailsSettingsLeaveModal
                    spaceData={spaceData}
                    closeModal={() => toggleLeaveSpaceModal()}
                    open={leaveSpaceModal}
                />
            )}
        </div>
    );
}

function SpaceDetailsSettingsMembers(props: { spaceData: Space; isOwner: boolean }) {
    const { spaceData, isOwner } = props;

    const [filter, setFilter] = useState({
        search: "",
        filter: "",
    });

    const [openRemoveMembersModal, setOpenRemoveMembersModal] = useToggle();
    const [member, setMember] = useState<{ fullName: string; id: string; isGuest: boolean } | null>(
        null
    );

    const members = normalizeMembers(spaceData);

    const filterOptions = [
        {
            label: "Everyone",
            value: "",
        },
        {
            label: "Guests",
            value: "isGuest",
        },
        {
            label: "Members",
            value: "isMember",
        },
    ];

    const filteredMembers = members.filter((member) => {
        return (
            (member?.fullName?.toLowerCase().includes(filter.search.toLowerCase()) ||
                member?.email?.toLowerCase().includes(filter.search.toLowerCase())) &&
            (filter.filter === "" ||
                (filter.filter === "isGuest" && member?.isGuest) ||
                (filter.filter === "isMember" && !member?.isGuest))
        );
    });

    return (
        <div>
            <div className="flex items-center gap-2">
                <SearchTextField
                    containerClassname="!w-full"
                    inputPlaceholder="Find members"
                    value={filter.search}
                    onChange={(e) => {
                        setFilter({ ...filter, search: e.target.value });
                    }}
                />

                <Dropdown
                    name="filter"
                    value={filter.filter}
                    handleChange={(name, value) => {
                        setFilter({ ...filter, [name]: value });
                    }}
                    dropdownOptions={[
                        ...filterOptions.map((option) => ({
                            label: <MenuItem>{option.label}</MenuItem>,

                            value: option.value,
                        })),
                    ]}
                    dropdown={
                        <div className="flex items-center justify-between rounded-[6px] py-[6px] px-[10px] gap-2 !border !border-slate-200 max-sm:hidden">
                            <div
                                className={`text-g-75 font-rocGroteskMedium text-[13px] whitespace-nowrap truncate inline-bloc`}
                            >
                                {filterOptions.find((item) => item.value === filter.filter)?.label}
                            </div>

                            <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>
                        </div>
                    }
                    wholeContainerClass="!w-fit max-sm:!w-full rounded-lg"
                    dropdownContainerClasses="shadow-none border border-slate-200 rounded-lg !w-w-[350px] min-w-full border border-slate-100 rounded !max-h-fit !pt-0"
                />
            </div>
            <Paper className="!p-0 overflow-hidden mt-[16px]">
                {filteredMembers.map((member, index) => {
                    return (
                        <div
                            key={index}
                            className={clsx(
                                "flex w-full items-center gap-2 p-[12px] py-[8px] bg-white hover:bg-slate-100 px-[16px] transition-colors",
                                index === filteredMembers.length - 1
                                    ? "!border-b-0"
                                    : "!border-b border-slate-100"
                            )}
                        >
                            <Avatar
                                src={member?.avatar}
                                alt={member?.fullName}
                                className="w-[24px] h-[24px]"
                            />
                            <div>
                                <p
                                    className={clsx("text-sm font-rocGroteskMedium font-semibold", {
                                        "text-[#62CC00]":
                                            member?.fullName === defaultSynthMember.name,
                                    })}
                                >
                                    {member?.fullName || member?.email || member?.businessName}
                                </p>
                                <p className="text-sm font-rocGroteskMedium text-slate-500">
                                    {member?.role}
                                </p>
                            </div>

                            <div>
                                {!!member?.isGuest && (
                                    <SpaceGuestBadge className="!text-[#0AAD0A] !bg-[#DCFCE7]" />
                                )}
                            </div>

                            <div className="flex-1" />

                            {!member?.isOwner &&
                                !member?.isSynth &&
                                isOwner &&
                                !spaceData?.isArchived && (
                                    <Dropdown
                                        dropdownOptions={[
                                            {
                                                label: <MenuItem>Remove Member</MenuItem>,
                                                value: null,
                                                action: () => {
                                                    setMember({
                                                        fullName: member?.fullName || member?.email,
                                                        id: member?.isGuest
                                                            ? member?.guestId
                                                            : member?.ldbId,
                                                        isGuest: member?.isGuest,
                                                    });
                                                    setOpenRemoveMembersModal();
                                                },
                                            },
                                        ]}
                                        dropdown={
                                            <IconButton
                                                color="secondary"
                                                className="bg-[#F4F5F7]"
                                                disabled={openRemoveMembersModal}
                                            >
                                                <i className="ri-more-2-fill"></i>
                                            </IconButton>
                                        }
                                        wholeContainerClass="!w-fit max-sm:!w-full rounded-lg !right-[0px]"
                                        dropdownContainerClasses="shadow-none border border-slate-200 rounded-lg !w-full !max-w-full min-w-full border border-slate-100 rounded !max-h-fit !pt-0 !right-[0px]"
                                    />
                                )}
                        </div>
                    );
                })}

                {openRemoveMembersModal && (
                    <SpaceDetailsRemoveMembersModal
                        spaceData={spaceData}
                        member={member}
                        open={openRemoveMembersModal}
                        closeModal={() => {
                            setOpenRemoveMembersModal();
                            setMember(null);
                        }}
                    />
                )}
            </Paper>
        </div>
    );
}

function SpaceDetailsSettingsSettings(props: { spaceData: Space; isOwner: boolean }) {
    const { spaceData, isOwner } = props;

    const [archiveSpaceModal, toggleArchiveSpaceModal] = useToggle();

    const [unarchiveSpaceMutation, unarchiveSpaceMutationResult] = useUnarchiveSpace();

    async function handleUnarchiveSpace() {
        try {
            await unarchiveSpaceMutation(spaceData?._id);
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title="Space unarchived"
                    message="This space has been successfully restored and is now active again."
                    type="success"
                />
            ));
        } catch (error) {
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title="Failed to Unarchive"
                    message={
                        error?.message ||
                        "Something went wrong while restoring this space. Please try again."
                    }
                    type="error"
                />
            ));
        }
    }

    return (
        <Paper className="flex flex-col justify-between gap-2">
            <div>
                <p className="text-sm font-rocGroteskMedium text-g-75">
                    {spaceData?.isArchived
                        ? "This space has been archived"
                        : "Archive space for everyone"}
                </p>
                <p className="text-xs font-rocGroteskMedium text-g-75 text-slate-500">
                    {spaceData?.isArchived
                        ? "You’re viewing an archived space. Content is read-only until it’s restored."
                        : "Archiving will close this space for everyone. No one will be able to post new messages or tasks, but all past conversations, files, and notes will remain accessible in read-only mode."}
                </p>
            </div>

            {spaceData?.isArchived ? (
                <Button
                    btnText="Unarchive space"
                    btnClassname="!w-fit whitespace-nowrap !py-[8px] !px-[12px]"
                    onClick={() => handleUnarchiveSpace()}
                    isLoading={unarchiveSpaceMutationResult.isLoading}
                />
            ) : (
                <Button
                    disabled={!isOwner || spaceData?.isArchived}
                    btnText="Archive space"
                    btnClassname="!w-fit whitespace-nowrap !py-[6px] !px-[12px] !bg-[#E4281D] !text-white"
                    onClick={() => toggleArchiveSpaceModal()}
                />
            )}
            {archiveSpaceModal && (
                <SpaceDetailsSettingsArchiveModal
                    spaceData={spaceData}
                    closeModal={() => toggleArchiveSpaceModal()}
                    open={archiveSpaceModal}
                />
            )}
        </Paper>
    );
}
